import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { KioskIcon } from "./KioskIcon";

export const KeyboardContext = createContext(null);
export const useKioskKeyboard = () => useContext(KeyboardContext);

const LETTERS = [
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["shift", "z", "x", "c", "v", "b", "n", "m", "backspace"],
  ["symbols", ",", "space", ".", "enter"],
];
const HINTS = { q: "1", w: "2", e: "3", r: "4", t: "5", y: "6", u: "7", i: "8", o: "9", p: "0" };
const SYMBOLS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["@", "#", "₹", "_", "&", "-", "+", "(", ")"],
  ["/", "*", "\"", "'", ":", ";", "!", "?", "backspace"],
  ["letters", ",", "space", ".", "enter"],
];
const UNITS = { shift: 1.5, backspace: 1.5, symbols: 1.5, letters: 1.5, enter: 1.5, space: 5 };

export const KEYBOARD_THEMES = {
  dark: { panel: "#1f252d", key: "#343b46", special: "#283039", text: "#f3f4f6", hint: "#9ca3af", enter: "#4b5563", pressed: "#5b6575" },
  light: { panel: "#d9dde3", key: "#ffffff", special: "#c3c8d0", text: "#111827", hint: "#6b7280", enter: "#9ca3af", pressed: "#e5e7eb" },
};

export function Keyboard({ node, width, height, onKey, interactive = true }) {
  const [mode, setMode] = useState("letters");
  const [shift, setShift] = useState(false);
  const [pressed, setPressed] = useState(null);
  const base = KEYBOARD_THEMES[node.keyboardTheme] || KEYBOARD_THEMES.dark;
  const t = {
    ...base,
    panel: node.keyboardPanelColor || base.panel,
    key: node.keyboardKeyColor || base.key,
    text: node.keyboardTextColor || base.text,
    enter: node.keyboardEnterColor || base.enter,
  };
  const W = Math.max(200, width || 760);
  const pad = Math.round(W * 0.016);
  const gap = Math.round(W * 0.009);
  const unit = (W - pad * 2 - gap * 9) / 10;
  const fitH = height ? (height - pad * 2 - gap * 3) / 4 : unit * 1.08;
  const keyH = Math.max(18, Math.min(unit * 1.08, fitH));
  const fs = Math.round(Math.min(unit, keyH) * 0.4);
  const rows = mode === "letters" ? LETTERS : SYMBOLS;

  const tap = k => {
    if (!interactive) return;
    setPressed(k);
    setTimeout(() => setPressed(p => (p === k ? null : p)), 110);
    if (k === "shift") { setShift(s => !s); return; }
    if (k === "symbols") { setMode("symbols"); return; }
    if (k === "letters") { setMode("letters"); return; }
    if (k === "space") { onKey?.(" "); return; }
    if (k === "backspace" || k === "enter") { onKey?.(k); return; }
    onKey?.(shift && mode === "letters" ? k.toUpperCase() : k);
    if (shift) setShift(false);
  };

  const label = k => {
    if (k === "shift") return <KioskIcon name="ArrowBigUp" size={fs * 1.15} color={t.text} strokeWidth={shift ? 2.6 : 1.8} />;
    if (k === "backspace") return <KioskIcon name="Delete" size={fs * 1.15} color={t.text} />;
    if (k === "enter") return <KioskIcon name="ArrowRight" size={fs * 1.15} color={t.text} />;
    if (k === "symbols") return "?123";
    if (k === "letters") return "ABC";
    if (k === "space") return "";
    return shift && mode === "letters" ? k.toUpperCase() : k;
  };

  return <div
    data-kiosk-keyboard="1"
    onPointerDown={e => e.preventDefault()}
    style={{ width: W, height: height || undefined, boxSizing: "border-box", padding: pad, background: t.panel, borderRadius: Math.round(unit * 0.32), boxShadow: "0 14px 40px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", justifyContent: "center", gap, fontFamily: node.styles?.fontFamily || "Inter, sans-serif", userSelect: "none", WebkitUserSelect: "none", touchAction: "manipulation", pointerEvents: interactive ? "auto" : "none" }}>
    {rows.map((row, ri) => (
      <div key={ri} style={{ display: "flex", gap, paddingLeft: ri === 1 && row.length === 9 ? (unit + gap) / 2 : 0, paddingRight: ri === 1 && row.length === 9 ? (unit + gap) / 2 : 0 }}>
        {row.map(k => {
          const special = k in UNITS && k !== "space";
          const bg = pressed === k ? t.pressed : k === "enter" ? t.enter : special ? t.special : t.key;
          return <div key={k}
            onPointerDown={e => { e.preventDefault(); e.stopPropagation(); tap(k); }}
            style={{ flex: `${UNITS[k] || 1} 1 0`, height: keyH, borderRadius: Math.round(Math.min(unit, keyH) * 0.16), background: bg, color: t.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: (k === "symbols" || k === "letters") ? fs * 0.75 : fs, position: "relative", cursor: "pointer", transition: "background 0.08s", boxShadow: k === "shift" && shift ? `inset 0 0 0 2px ${t.text}` : "0 1px 0 rgba(0,0,0,0.25)" }}>
            {mode === "letters" && HINTS[k] && <span style={{ position: "absolute", top: Math.round(keyH * 0.05), right: Math.round(unit * 0.12), fontSize: fs * 0.55, color: t.hint }}>{HINTS[k]}</span>}
            {label(k)}
          </div>;
        })}
      </div>
    ))}
  </div>;
}

export function KeyboardHost({ hasKeyboard, vars, onVarChange, resetKey, children }) {
  const [focused, setFocused] = useState(null);
  const varsRef = useRef(vars);
  varsRef.current = vars;

  const focus = useCallback(node => {
    if (!node?.inputVar) return;
    setFocused(f => (f && f.id === node.id ? f : { id: node.id, varName: node.inputVar }));
  }, []);
  const blur = useCallback(() => {
    setFocused(null);
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  }, []);

  useEffect(() => { setFocused(null); }, [resetKey]);

  useEffect(() => {
    if (!focused) return undefined;
    const onDown = e => {
      const el = e.target;
      if (el && el.closest && (el.closest("[data-kiosk-keyboard]") || el.closest("[data-kiosk-input]"))) return;
      setFocused(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [focused]);

  const type = useCallback((varName, k) => {
    if (!varName) return;
    const cur = String(varsRef.current?.[varName] ?? "");
    const next = k === "backspace" ? cur.slice(0, -1) : cur + k;
    varsRef.current = { ...varsRef.current, [varName]: next };
    onVarChange?.(varName, next);
  }, [onVarChange]);

  const ctx = useMemo(() => ({ hasKeyboard: !!hasKeyboard, focused, focus, blur, type }), [hasKeyboard, focused, focus, blur, type]);
  return <KeyboardContext.Provider value={ctx}>{children}</KeyboardContext.Provider>;
}

export function KeyboardElement({ node, onEnter, interactive = true }) {
  const kb = useKioskKeyboard();
  const s = node.styles || {};
  if (!interactive) return <Keyboard node={node} width={s.width} height={s.height} interactive={false} />;
  if (!kb) return null;
  const target = node.keyboardTarget || "";
  const focusedVar = kb.focused?.varName || "";
  const matches = !!focusedVar && (!target || target === focusedVar);
  const visible = node.keyboardShowAlways || matches;
  if (!visible) return null;
  const varName = matches ? focusedVar : target;
  return <div style={{ animation: node.keyboardShowAlways ? undefined : "kioskKbIn 0.18s ease-out" }}>
    <style>{"@keyframes kioskKbIn{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}"}</style>
    <Keyboard node={node} width={s.width} height={s.height} onKey={k => {
      if (k === "enter") {
        if (!node.keyboardShowAlways || matches) kb.blur();
        onEnter?.();
        return;
      }
      kb.type(varName, k);
    }} />
  </div>;
}
